import React, { useState } from 'react';
import {
  Box,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography,
} from '@mui/material';
import { useApiGet } from '../../utils/httpClientTs';
import { getStripeCharges } from '../../endpoints/financePayments';
import ServerSideTable, {
  ServerSideTableColumn,
} from '../../Components/ServerSideTable';
import type { StripeChargeRow } from '../../types/financePayments';

const fmtMoney = (n: number) =>
  new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);

const PaymentsStripePage: React.FC = () => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [source, setSource] = useState<string>('');

  const { data, isLoading } = useApiGet(
    ['finance-payments-stripe', page, rowsPerPage, source],
    () =>
      getStripeCharges({
        page: page + 1,
        page_size: rowsPerPage,
        source: source || undefined,
      }),
    { enabled: true, retry: 1 }
  );

  const tableRows = data?.data ?? [];
  const pagination = data?.pagination;

  const columns: ServerSideTableColumn<StripeChargeRow>[] = [
    {
      id: 'payment_id',
      label: 'Payment',
      width: '22%',
      firstCell: true,
      sortable: false,
      render: (_v, row) => (
        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
          {row.payment_id}
        </Typography>
      ),
    },
    {
      id: 'source',
      label: 'Source',
      width: '12%',
      sortable: false,
      render: (_v, row) => row.source,
    },
    {
      id: 'amount',
      label: 'Amount',
      width: '10%',
      sortable: false,
      render: (_v, row) => fmtMoney(row.amount),
    },
    {
      id: 'fee',
      label: 'Fee',
      width: '8%',
      sortable: false,
      render: (_v, row) => (row.fee != null ? fmtMoney(row.fee) : '—'),
    },
    {
      id: 'status',
      label: 'Status',
      width: '12%',
      sortable: false,
      render: (_v, row) => row.status,
    },
    {
      id: 'created_at',
      label: 'Created',
      width: '14%',
      sortable: false,
      render: (_v, row) =>
        row.created_at ? new Date(row.created_at).toLocaleString() : '—',
    },
  ];

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 220 }}>
          <InputLabel id="stripe-source-label">Source</InputLabel>
          <Select
            labelId="stripe-source-label"
            label="Source"
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setPage(0);
            }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="donations">Donations</MenuItem>
            <MenuItem value="chapter_supplies">Chapter supplies</MenuItem>
            <MenuItem value="other">Other</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          Read-only list for reconciliation. Deposit workflow is not in scope
          yet.
        </Typography>
      </Box>
      <ServerSideTable<StripeChargeRow>
        data={tableRows}
        columns={columns}
        loading={isLoading}
        page={page}
        rowsPerPage={rowsPerPage}
        totalCount={pagination?.total_count ?? 0}
        totalPages={pagination?.total_pages ?? 0}
        hasNext={pagination?.has_next ?? false}
        hasPrev={pagination?.has_prev ?? false}
        order="desc"
        orderBy="created_at"
        onPageChange={setPage}
        onRowsPerPageChange={(n) => {
          setRowsPerPage(n);
          setPage(0);
        }}
        onSort={() => {}}
        emptyMessage="No Stripe charges found."
      />
    </Box>
  );
};

export default PaymentsStripePage;
